/**
 * EpilepsyJourney.jsx — Scrollytelling journey of a baby with epilepsy
 *
 * Act 1: First seizure — emotional intro
 * Act 2: The diagnostic gauntlet — tests, wait times, delays
 * Act 3: Insurance battles — denials, appeals, costs
 * Act 4: The full timeline — cumulative months to diagnosis
 */

import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ContactFooter from '../components/ContactFooter';

gsap.registerPlugin(ScrollTrigger);

const FONT_URL =
  'https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=IBM+Plex+Mono:wght@300;400;500&family=Syne:wght@400;500;700&display=swap';

// ─── ACT 1: THE FIRST SEIZURE ────────────────────────────────────────────────
const ACT1_LINES = [
  { at: 0,    text: 'This is Mia.\nShe is four months old.' },
  { at: 0.15, text: 'One morning, her body stiffens.\nHer eyes roll back.\nIt lasts twelve seconds.\nIt changes everything.' },
  { at: 0.30, text: 'Her parents rush to the emergency room.\nThe ER doctor says: "It could be a febrile seizure.\nLet\'s wait and see."' },
  { at: 0.45, text: 'It happens again.\nAnd again.\nThree seizures in one week.' },
  { at: 0.60, text: 'A referral to a pediatric neurologist.\nThe earliest appointment is six weeks away.' },
  { at: 0.78, text: 'Six weeks of watching.\nSix weeks of not sleeping.\nSix weeks of wondering\nif your baby is okay.' },
];

// ─── ACT 2: THE DIAGNOSTIC GAUNTLET ─────────────────────────────────────────
const TESTS = [
  {
    id: 'eeg',
    name: 'EEG Monitoring',
    duration: '2–4 weeks',
    daysMin: 14,
    daysMax: 28,
    color: '#b48cfa',
    icon: '⚡',
    desc: 'Electrodes measure brain activity. Routine EEG may miss seizures — often needs 24-72hr extended monitoring. Wait time for pediatric EEG slots: 2-4 weeks.',
    insurance: 'Usually covered, but extended monitoring may require prior authorization.',
  },
  {
    id: 'mri',
    name: 'Brain MRI',
    duration: '3–6 weeks',
    daysMin: 21,
    daysMax: 42,
    color: '#5bc8f5',
    icon: '🧲',
    desc: 'Structural scan of the brain. Infants need sedation, requiring anesthesia scheduling. Limited pediatric MRI slots create bottlenecks.',
    insurance: 'Prior authorization required by most insurers. Average approval time: 5-14 business days.',
  },
  {
    id: 'blood',
    name: 'Metabolic Labs',
    duration: '1–3 weeks',
    daysMin: 7,
    daysMax: 21,
    color: '#00e5b0',
    icon: '🩸',
    desc: 'Blood and urine panels to rule out metabolic disorders — amino acids, organic acids, lactate, ammonia. Results take 1-3 weeks depending on the panel.',
    insurance: 'Typically covered. Specialized panels may need justification letters.',
  },
  {
    id: 'genetic',
    name: 'Epilepsy Gene Panel',
    duration: '6–12 weeks',
    daysMin: 42,
    daysMax: 84,
    color: '#f5a623',
    icon: '🧬',
    desc: 'Tests 100-500 genes associated with epilepsy. Results take 6-12 weeks. May need to be sent to a specialized lab.',
    insurance: 'Frequently denied on first submission. Appeal process adds 4-8 weeks. Out-of-pocket: $1,500-$5,000.',
  },
  {
    id: 'wes',
    name: 'Whole Exome Sequencing',
    duration: '12–16 weeks',
    daysMin: 84,
    daysMax: 112,
    color: '#ff6b8a',
    icon: '🔬',
    desc: 'When gene panels come back negative: sequence all protein-coding genes. Requires trio testing (baby + both parents). The most comprehensive — and the slowest.',
    insurance: 'Denied 60% of the time. Appeals take 6-12 weeks. Out-of-pocket: $5,000-$15,000. Many families give up here.',
  },
];

// ─── ACT 3: INSURANCE BATTLES ────────────────────────────────────────────────
const INSURANCE_EVENTS = [
  {
    week: 8,
    type: 'denial',
    title: 'MRI Prior Auth Submitted',
    detail: 'Neurologist submits prior authorization for brain MRI with sedation.',
    cost: null,
    color: '#5bc8f5',
  },
  {
    week: 10,
    type: 'denial',
    title: 'MRI Prior Auth — DENIED',
    detail: '"Not medically necessary at this time. Recommend continued observation." The insurer wants more seizure documentation first.',
    cost: null,
    color: '#ff6b8a',
  },
  {
    week: 11,
    type: 'appeal',
    title: 'Neurologist Files Appeal',
    detail: 'Peer-to-peer review requested. The neurologist spends 45 minutes on hold to speak with the insurance company\'s physician reviewer.',
    cost: null,
    color: '#f5a623',
  },
  {
    week: 14,
    type: 'approval',
    title: 'MRI Finally Approved',
    detail: 'Six weeks after the first request. MRI scheduled for 3 weeks out — pediatric anesthesia slots are limited.',
    cost: null,
    color: '#00e5b0',
  },
  {
    week: 18,
    type: 'denial',
    title: 'Genetic Panel — DENIED',
    detail: '"Experimental and investigational." The insurer classifies the epilepsy gene panel as not standard of care, despite guideline recommendations.',
    cost: '$3,200',
    color: '#ff6b8a',
  },
  {
    week: 20,
    type: 'appeal',
    title: 'Family Files Appeal with Letter',
    detail: 'The family gathers medical records, writes a personal letter, and the neurologist provides a letter of medical necessity. Total time spent: 12+ hours across the family and clinical team.',
    cost: null,
    color: '#f5a623',
  },
  {
    week: 26,
    type: 'denial',
    title: 'Appeal — DENIED Again',
    detail: '"The member has not exhausted standard diagnostic workup." The insurer requires failed medication trials before approving genetic testing.',
    cost: null,
    color: '#ff6b8a',
  },
  {
    week: 27,
    type: 'cost',
    title: 'Family Pays Out-of-Pocket',
    detail: 'Mia\'s family decides they can\'t wait any longer. They pay $3,200 out of pocket for the genetic panel. They start a GoFundMe.',
    cost: '$3,200',
    color: '#ff6b8a',
  },
  {
    week: 36,
    type: 'denial',
    title: 'Whole Exome — DENIED',
    detail: 'Gene panel was inconclusive. WES is recommended but denied: "Prior gene panel did not yield actionable results; further genetic testing not indicated."',
    cost: '$9,500',
    color: '#ff6b8a',
  },
  {
    week: 44,
    type: 'approval',
    title: 'WES Approved After External Review',
    detail: 'After two appeals and an external independent review, WES is finally approved. Eight months after the neurologist first recommended genetic testing.',
    cost: null,
    color: '#00e5b0',
  },
];

// ─── ACT 4: JOURNEY MILESTONES ───────────────────────────────────────────────
const MILESTONES = [
  { month: 0,  label: 'First seizure',                         note: 'Mia is 4 months old. Her parents call 911.', color: '#ff6b8a' },
  { month: 0.5, label: 'ER visit — "wait and see"',            note: 'Told it could be febrile. No referral yet.', color: '#5a637a' },
  { month: 1,  label: 'Multiple seizures, PCP referral',       note: 'Pediatrician refers to neurology. Earliest available: 6 weeks.', color: '#f5a623' },
  { month: 2.5, label: 'First neurology appointment',          note: 'EEG ordered. Earliest slot: 3 weeks.', color: '#b48cfa' },
  { month: 3.5, label: 'EEG confirms epileptiform activity',   note: 'Abnormal. MRI and labs ordered. MRI prior auth submitted.', color: '#b48cfa' },
  { month: 4,  label: 'MRI prior authorization denied',        note: '"Not medically necessary." Appeal filed.', color: '#ff6b8a' },
  { month: 5,  label: 'MRI approved on appeal',                note: 'Scheduled 3 weeks out due to anesthesia slots.', color: '#00e5b0' },
  { month: 6,  label: 'MRI completed — normal',                note: 'No structural cause found. Genetic testing recommended.', color: '#5bc8f5' },
  { month: 6.5, label: 'Genetic panel denied by insurance',    note: '"Experimental." Family begins appeal process.', color: '#ff6b8a' },
  { month: 7,  label: 'First anti-seizure medication trial',   note: 'Levetiracetam started. Side effects: irritability, poor feeding.', color: '#f5a623' },
  { month: 8,  label: 'Second medication trial',               note: 'Switched to oxcarbazepine. Seizures continue.', color: '#f5a623' },
  { month: 9,  label: 'Genetic appeal denied again',           note: 'Family pays $3,200 out of pocket.', color: '#ff6b8a' },
  { month: 11, label: 'Gene panel results: inconclusive',      note: 'Variant of uncertain significance found. WES recommended.', color: '#f5a623' },
  { month: 12, label: 'Whole exome sequencing denied',          note: '"Prior testing did not yield actionable results."', color: '#ff6b8a' },
  { month: 14, label: 'Third medication trial',                 note: 'Clobazam added. Sedation, developmental regression.', color: '#f5a623' },
  { month: 16, label: 'WES finally approved after external review', note: 'Eight months after first genetic test request.', color: '#00e5b0' },
  { month: 18, label: 'WES results: ALDH7A1 mutation confirmed', note: 'Pyridoxine-dependent epilepsy. Treatable with vitamin B6.', color: '#00e5b0' },
  { month: 18.5, label: 'Pyridoxine started — seizures stop',  note: 'Within 48 hours. Eighteen months of seizures. Eighteen months of medications with side effects. The answer was a vitamin.', color: '#00e5b0' },
];

const CUMULATIVE_COSTS = [
  { label: 'ER visits (3)',              amount: 4500 },
  { label: 'Specialist copays (12+)',    amount: 1800 },
  { label: 'EEG monitoring',            amount: 800 },
  { label: 'MRI with sedation copay',   amount: 1200 },
  { label: 'Genetic panel (out-of-pocket)', amount: 3200 },
  { label: 'Anti-seizure medications',   amount: 2400 },
  { label: 'Lost wages (parent)',        amount: 12000 },
  { label: 'Travel to specialists',     amount: 1600 },
  { label: 'GoFundMe / family loans',   amount: 5000 },
];

// ─── SOURCES ─────────────────────────────────────────────────────────────────
const SOURCES = [
  {
    title: 'Epilepsy prevalence and lifetime risk',
    body: 'The Centers for Disease Control and Prevention (CDC) and the Epilepsy Foundation report that roughly 1 in 26 people in the US will develop epilepsy in their lifetime, and that epilepsy affects approximately 470,000 children nationwide.',
  },
  {
    title: 'Pyridoxine-dependent epilepsy (PDE)',
    body: 'PDE is a rare genetic epilepsy caused by mutations in the ALDH7A1 gene and is treatable with pyridoxine (vitamin B6) supplementation. See the National Organization for Rare Disorders (NORD) and published clinical literature on ALDH7A1-PDE.',
  },
  {
    title: 'Diagnostic and genetic testing pathway',
    body: 'The test types, their sequencing, and the typical wait ranges shown here reflect general clinical practice for pediatric epilepsy workup — EEG, brain MRI, metabolic panels, epilepsy gene panels, and whole exome sequencing — as described by professional guidance from organizations such as the American Academy of Neurology and the International League Against Epilepsy.',
  },
  {
    title: 'A note on this story',
    body: '"Mia" is an illustrative composite, not a specific individual. The timeline, insurance denials, appeal durations, and dollar figures are representative scenarios drawn from commonly reported patterns in the rare-disease diagnostic odyssey. They are intended to communicate the scale of delay and cost — they are not measurements of a single verified case, and the specific numbers should not be cited as precise statistics.',
  },
];

// ─── SHARED STYLES ───────────────────────────────────────────────────────────
const S = {
  mono: { fontFamily: "'IBM Plex Mono', monospace" },
  serif: { fontFamily: "'Instrument Serif', serif" },
  syne: { fontFamily: "'Syne', sans-serif" },
  label: { fontFamily: "'IBM Plex Mono', monospace", fontSize: '0.6rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#ff6b8a', marginBottom: '1.2rem' },
  muted: { color: '#5a637a' },
  divider: { borderTop: '1px solid rgba(255,255,255,0.05)' },
};

// ─── UTILS ───────────────────────────────────────────────────────────────────
function formatMoney(n) {
  return '$' + n.toLocaleString();
}

// ─── BRIDGE ──────────────────────────────────────────────────────────────────
function Bridge({ eyebrow, heading, sub, headingColor = '#dde3f0' }) {
  return (
    <div style={{ textAlign: 'center', padding: '8rem 2rem', background: '#07080a', ...S.divider }}>
      <div style={{ ...S.label, marginBottom: '1.5rem' }}>{eyebrow}</div>
      <p style={{ ...S.serif, fontSize: 'clamp(2rem, 4vw, 3.2rem)', lineHeight: 1.15, maxWidth: '680px', margin: '0 auto 1.5rem', color: headingColor }}
        dangerouslySetInnerHTML={{ __html: heading }} />
      <p style={{ ...S.mono, fontSize: 'clamp(0.75rem, 1.2vw, 0.88rem)', ...S.muted, maxWidth: '520px', margin: '0 auto', lineHeight: 1.85 }}>
        {sub}
      </p>
    </div>
  );
}

// ─── HOW YOU CAN HELP ────────────────────────────────────────────────────────
const HELP_WAYS = [
  { t: 'Give', d: 'Fund research into pediatric epilepsy and support for families navigating diagnosis.' },
  { t: 'Advocate', d: 'Push for insurance reform so no family pays $3,200 out of pocket for a genetic test.' },
  { t: 'Share', d: "Tell Mia's story. Awareness shortens the road to diagnosis for the next child." },
];
const HELP_FOUNDATIONS = [
  { name: 'Epilepsy Foundation', desc: '24/7 helpline, family support, and advocacy for epilepsy care.', url: 'https://www.epilepsy.com' },
  { name: 'CURE Epilepsy', desc: 'Funds research aimed at a cure and at no seizures, no side effects.', url: 'https://www.cureepilepsy.org' },
  { name: 'Child Neurology Foundation', desc: 'Supports families navigating childhood neurologic conditions.', url: 'https://www.childneurologyfoundation.org' },
  { name: 'National Organization for Rare Disorders (NORD)', desc: 'Patient assistance and advocacy across rare conditions.', url: 'https://rarediseases.org' },
];

function HelpSection() {
  return (
    <div style={{ background: 'linear-gradient(to bottom, #07080a 0%, #0d0a0c 100%)', borderTop: '1px solid rgba(255,107,138,0.18)', padding: '7rem 2rem' }}>
      <div style={{ maxWidth: '720px', margin: '0 auto' }}>
        <div style={{ ...S.label, marginBottom: '1.2rem' }}>How You Can Help</div>
        <p style={{ ...S.serif, fontSize: 'clamp(2rem, 5vw, 3.4rem)', lineHeight: 1.1, letterSpacing: '-0.02em', marginBottom: '1.6rem' }}>
          No family should have to wait <em style={{ color: '#ff6b8a', fontStyle: 'italic' }}>this long.</em>
        </p>
        <p style={{ ...S.mono, fontSize: '0.78rem', color: '#5a637a', lineHeight: 1.9, marginBottom: '3.5rem', maxWidth: '540px' }}>
          The delays in Mia's story are not inevitable. They are fixable &mdash; with funding, research, and pressure on a system that makes families fight for answers.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '1.6rem', marginBottom: '4rem' }}>
          {HELP_WAYS.map((w) => (
            <div key={w.t} style={{ borderTop: '2px solid #ff6b8a', paddingTop: '1rem' }}>
              <div style={{ ...S.syne, fontSize: '1rem', fontWeight: 600, color: '#dde3f0', marginBottom: '0.5rem' }}>{w.t}</div>
              <div style={{ ...S.mono, fontSize: '0.72rem', color: '#5a637a', lineHeight: 1.8 }}>{w.d}</div>
            </div>
          ))}
        </div>

        <div style={{ ...S.label, marginBottom: '1.2rem', color: '#5a637a' }}>Foundations Worth Supporting</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          {HELP_FOUNDATIONS.map((f) => (
            <a key={f.name} href={f.url} target="_blank" rel="noopener noreferrer"
               style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1.5rem', padding: '1.3rem 1.5rem', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '10px', textDecoration: 'none', transition: 'border-color 0.18s ease, background 0.18s ease' }}
               onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(255,107,138,0.5)'; e.currentTarget.style.background = 'rgba(255,107,138,0.05)'; }}
               onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}>
              <div>
                <div style={{ ...S.syne, fontSize: '0.9rem', fontWeight: 600, color: '#dde3f0', marginBottom: '0.3rem' }}>{f.name}</div>
                <div style={{ ...S.mono, fontSize: '0.7rem', color: '#5a637a', lineHeight: 1.65 }}>{f.desc}</div>
              </div>
              <span style={{ ...S.mono, fontSize: '1rem', color: '#ff6b8a', flexShrink: 0 }}>&rarr;</span>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── SOURCES SECTION ─────────────────────────────────────────────────────────
function SourcesSection() {
  return (
    <div style={{ background: '#07080a', borderTop: '1px solid rgba(255,255,255,0.05)', padding: '5rem 2rem 6rem' }}>
      <div style={{ maxWidth: '720px', margin: '0 auto' }}>
        <div style={{ ...S.label, marginBottom: '0.6rem' }}>Sources &amp; Notes</div>
        <p style={{ ...S.mono, fontSize: '0.7rem', color: '#3a4255', lineHeight: 1.7, marginBottom: '2.5rem' }}>
          This page is an educational illustration. Verifiable claims are attributed below; the patient narrative is a composite.
        </p>
        {SOURCES.map((s) => (
          <div key={s.title} style={{ marginBottom: '1.8rem' }}>
            <div style={{ ...S.syne, fontSize: '0.82rem', fontWeight: 600, color: '#dde3f0', marginBottom: '0.4rem' }}>
              {s.title}
            </div>
            <div style={{ ...S.mono, fontSize: '0.7rem', color: '#5a637a', lineHeight: 1.8 }}>
              {s.body}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── DAY COUNTER ─────────────────────────────────────────────────────────────
function DayCounter({ days }) {
  const months = Math.floor(days / 30);
  const remainDays = days % 30;
  return (
    <div style={{ ...S.mono, fontSize: 'clamp(1.2rem, 3vw, 2rem)', color: '#ff6b8a', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
      {months > 0 && <>{months} <span style={{ fontSize: '0.6em', color: '#3a4255' }}>month{months !== 1 ? 's' : ''}</span> </>}
      {remainDays > 0 && <>{remainDays} <span style={{ fontSize: '0.6em', color: '#3a4255' }}>day{remainDays !== 1 ? 's' : ''}</span></>}
      {days === 0 && <span style={{ fontSize: '0.6em', color: '#3a4255' }}>Day 1</span>}
      <div style={{ fontSize: '0.45em', color: '#3a4255', marginTop: '0.3rem', letterSpacing: '0.15em' }}>
        SINCE FIRST SEIZURE
      </div>
    </div>
  );
}

// ─── HEARTBEAT CANVAS ────────────────────────────────────────────────────────
function HeartbeatCanvas({ intensity = 0, spiking = false }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const timeRef = useRef(0);
  const intensityRef = useRef(intensity);
  const spikingRef = useRef(spiking);

  useEffect(() => { intensityRef.current = intensity; }, [intensity]);
  useEffect(() => { spikingRef.current = spiking; }, [spiking]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
    };
    resize();
    window.addEventListener('resize', resize);

    const ctx = canvas.getContext('2d');
    const draw = () => {
      const W = canvas.width, H = canvas.height;
      const dpr = window.devicePixelRatio;
      ctx.clearRect(0, 0, W, H);
      timeRef.current += 0.02;

      const int = intensityRef.current;
      const spike = spikingRef.current;
      const numLines = Math.max(1, Math.floor(int * 8));

      for (let l = 0; l < numLines; l++) {
        ctx.beginPath();
        const yBase = H * (0.2 + (l / numLines) * 0.6);
        const alpha = spike ? 0.6 + Math.random() * 0.3 : 0.15 + int * 0.25;
        ctx.strokeStyle = spike
          ? `rgba(255, 107, 138, ${alpha})`
          : `rgba(180, 140, 250, ${alpha})`;
        ctx.lineWidth = (spike ? 2.5 : 1.2) * dpr;

        for (let x = 0; x < W; x += 2) {
          const t = timeRef.current + l * 0.7;
          const freq = spike ? 12 + Math.random() * 8 : 3 + int * 4;
          const amp = spike
            ? H * (0.08 + Math.random() * 0.15)
            : H * (0.01 + int * 0.06);
          const y = yBase + Math.sin((x / W) * freq * Math.PI + t) * amp
            + (spike ? Math.random() * amp * 0.5 : 0);
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }

      rafRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => { window.removeEventListener('resize', resize); cancelAnimationFrame(rafRef.current); };
  }, []);

  return <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.6 }} />;
}

// ─── TEST CARD ───────────────────────────────────────────────────────────────
function TestCard({ test, progress, isActive }) {
  const fillWidth = Math.min(1, progress) * 100;
  const isComplete = progress >= 1;

  return (
    <div style={{
      padding: '1.2rem 1.5rem',
      background: isActive ? `${test.color}10` : 'rgba(255,255,255,0.02)',
      border: `1px solid ${isActive ? test.color + '40' : 'rgba(255,255,255,0.06)'}`,
      borderRadius: '2px',
      transition: 'all 0.4s',
      opacity: progress > 0 ? 1 : 0.4,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <span style={{ fontSize: '1.1rem' }}>{test.icon}</span>
          <span style={{ ...S.syne, fontWeight: 600, fontSize: '0.85rem', color: isActive ? test.color : '#dde3f0' }}>
            {test.name}
          </span>
        </div>
        <span style={{ ...S.mono, fontSize: '0.65rem', color: test.color, letterSpacing: '0.08em' }}>
          {test.duration}
        </span>
      </div>

      {/* Progress bar */}
      <div style={{ height: '3px', background: 'rgba(255,255,255,0.06)', borderRadius: '2px', marginBottom: '0.6rem', overflow: 'hidden' }}>
        <div style={{
          height: '100%', width: `${fillWidth}%`,
          background: isComplete ? '#00e5b0' : test.color,
          transition: 'width 0.3s, background 0.3s',
          borderRadius: '2px',
        }} />
      </div>

      <p style={{ ...S.mono, fontSize: '0.68rem', color: '#5a637a', lineHeight: 1.6, marginBottom: '0.5rem' }}>
        {test.desc}
      </p>
      <p style={{ ...S.mono, fontSize: '0.6rem', color: '#ff6b8a', lineHeight: 1.5, fontStyle: 'italic' }}>
        {test.insurance}
      </p>
    </div>
  );
}

// ─── INSURANCE TIMELINE ──────────────────────────────────────────────────────
function InsuranceTimeline({ revealCount }) {
  const shown = INSURANCE_EVENTS.slice(0, revealCount);
  const TYPE_ICONS = { denial: '✕', appeal: '↻', approval: '✓', cost: '$' };
  const TYPE_LABELS = { denial: 'Denied', appeal: 'Appeal', approval: 'Approved', cost: 'Out-of-Pocket' };

  return (
    <div style={{ position: 'relative', paddingBottom: '4rem' }}>
      <div style={{
        position: 'absolute', left: '50%', top: 0, bottom: 0,
        width: '1px', background: 'rgba(255,255,255,0.06)',
        transform: 'translateX(-50%)',
      }} />

      {shown.map((evt, i) => {
        const side = i % 2 === 0 ? 'right' : 'left';
        const prevWeek = i > 0 ? INSURANCE_EVENTS[i - 1].week : 0;
        const weekGap = Math.max(0, evt.week - prevWeek);
        const marginTop = i === 0 ? '1rem' : `${Math.max(14, weekGap * 8)}px`;

        return (
          <div key={`${evt.week}-${evt.title}`} style={{
            position: 'relative',
            display: 'flex',
            justifyContent: side === 'left' ? 'flex-start' : 'flex-end',
            marginTop,
            opacity: 0,
            animation: 'fadeInUp 0.5s ease both',
          }}>
            <div style={{
              position: 'absolute', left: '50%', top: '1rem',
              width: '24px', height: '24px', borderRadius: '50%',
              background: evt.color,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transform: 'translateX(-50%)', zIndex: 1,
              fontSize: '0.7rem', fontWeight: 700, color: '#07080a',
              boxShadow: `0 0 12px ${evt.color}66`,
            }}>
              {TYPE_ICONS[evt.type]}
            </div>

            <div style={{
              width: '44%',
              padding: '0.8rem 1rem',
              background: `${evt.color}10`,
              border: `1px solid ${evt.color}30`,
              borderRadius: '2px',
              textAlign: side === 'right' ? 'left' : 'right',
            }}>
              <div style={{
                ...S.mono, fontSize: '0.55rem', letterSpacing: '0.1em',
                color: evt.color, marginBottom: '0.15rem', textTransform: 'uppercase',
              }}>
                Week {evt.week} · {TYPE_LABELS[evt.type]}
              </div>
              <div style={{ ...S.syne, fontSize: '0.85rem', color: '#dde3f0', lineHeight: 1.3, marginBottom: '0.3rem', fontWeight: 500 }}>
                {evt.title}
              </div>
              <div style={{ ...S.mono, fontSize: '0.68rem', color: '#5a637a', lineHeight: 1.6 }}>
                {evt.detail}
              </div>
              {evt.cost && (
                <div style={{ ...S.mono, fontSize: '0.8rem', color: '#ff6b8a', marginTop: '0.4rem', fontWeight: 500 }}>
                  {evt.cost}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── JOURNEY TIMELINE (ACT 4) ────────────────────────────────────────────────
function JourneyTimeline({ revealCount }) {
  const shown = MILESTONES.slice(0, revealCount);

  return (
    <div style={{ position: 'relative', paddingBottom: '4rem' }}>
      <div style={{
        position: 'absolute', left: '50%', top: 0, bottom: 0,
        width: '2px',
        background: 'linear-gradient(to bottom, rgba(255,107,138,0.3), rgba(0,229,176,0.3))',
        transform: 'translateX(-50%)',
      }} />

      {shown.map((m, i) => {
        const side = i % 2 === 0 ? 'right' : 'left';
        const prevMonth = i > 0 ? MILESTONES[i - 1].month : 0;
        const monthGap = Math.max(0, m.month - prevMonth);
        const marginTop = i === 0 ? '1rem' : `${Math.max(14, monthGap * 40)}px`;

        return (
          <div key={`${m.month}-${m.label}`} style={{
            position: 'relative',
            display: 'flex',
            justifyContent: side === 'left' ? 'flex-start' : 'flex-end',
            marginTop,
            opacity: 0,
            animation: 'fadeInUp 0.5s ease both',
          }}>
            <div style={{
              position: 'absolute', left: '50%', top: '1rem',
              width: '10px', height: '10px', borderRadius: '50%',
              background: m.color, boxShadow: `0 0 10px ${m.color}`,
              transform: 'translateX(-50%)', zIndex: 1,
            }} />

            <div style={{
              width: '44%',
              padding: '0.7rem 1rem',
              background: `${m.color}10`,
              border: `1px solid ${m.color}30`,
              borderRadius: '2px',
              textAlign: side === 'right' ? 'left' : 'right',
            }}>
              <div style={{
                ...S.mono, fontSize: '0.55rem', letterSpacing: '0.1em',
                color: m.color, marginBottom: '0.15rem', textTransform: 'uppercase',
              }}>
                Month {m.month}
              </div>
              <div style={{ ...S.syne, fontSize: '0.82rem', color: '#dde3f0', lineHeight: 1.3, marginBottom: '0.25rem', fontWeight: 500 }}>
                {m.label}
              </div>
              <div style={{ ...S.mono, fontSize: '0.66rem', color: '#5a637a', lineHeight: 1.6 }}>
                {m.note}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
export default function EpilepsyJourney() {
  const wrapRef = useRef(null);
  const act1Ref = useRef(null);
  const act2Ref = useRef(null);
  const act3Ref = useRef(null);
  const act3PanelRef = useRef(null);
  const act4Ref = useRef(null);
  const act4PanelRef = useRef(null);
  const copyRef = useRef(null);
  const isFirst = useRef(true);

  const [copyIndex, setCopyIndex] = useState(0);
  const [dayCount, setDayCount] = useState(0);
  const [brainIntensity, setBrainIntensity] = useState(0);
  const [isSpiking, setIsSpiking] = useState(false);
  const [act1Opacity, setAct1Opacity] = useState(1);

  // Act 2
  const [act2Opacity, setAct2Opacity] = useState(0);
  const [activeTestIdx, setActiveTestIdx] = useState(-1);
  const [testProgresses, setTestProgresses] = useState(TESTS.map(() => 0));

  // Act 3
  const [act3Opacity, setAct3Opacity] = useState(0);
  const [insuranceReveal, setInsuranceReveal] = useState(0);

  // Act 4
  const [act4Opacity, setAct4Opacity] = useState(0);
  const [journeyReveal, setJourneyReveal] = useState(0);

  useEffect(() => {
    if (!document.querySelector(`link[href="${FONT_URL}"]`)) {
      const l = Object.assign(document.createElement('link'), { rel: 'stylesheet', href: FONT_URL });
      document.head.appendChild(l);
    }
    const root = document.getElementById('root');
    const prev = {
      bodyOverflow: document.body.style.overflow,
      bodyHeight: document.body.style.minHeight,
      rootOverflow: root?.style.overflow,
      rootHeight: root?.style.height,
    };
    document.body.style.overflow = 'visible';
    document.body.style.minHeight = 'auto';
    if (root) { root.style.overflow = 'visible'; root.style.height = 'auto'; }
    return () => {
      document.body.style.overflow = prev.bodyOverflow;
      document.body.style.minHeight = prev.bodyHeight;
      if (root) { root.style.overflow = prev.rootOverflow; root.style.height = prev.rootHeight; }
    };
  }, []);

  useEffect(() => {
    const el = act3PanelRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [insuranceReveal]);

  useEffect(() => {
    const el = act4PanelRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [journeyReveal]);

  useEffect(() => {
    if (isFirst.current) { isFirst.current = false; return; }
    if (!copyRef.current) return;
    gsap.fromTo(copyRef.current, { opacity: 0, y: 12 }, { opacity: 1, y: 0, duration: 0.45, ease: 'power2.out' });
  }, [copyIndex]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // ACT 1: First seizure narrative
      ScrollTrigger.create({
        trigger: act1Ref.current,
        start: 'top top', end: 'bottom top', scrub: 0.6,
        onUpdate: (self) => {
          const p = self.progress;
          let idx = 0;
          for (let i = ACT1_LINES.length - 1; i >= 0; i--) {
            if (p >= ACT1_LINES[i].at) { idx = i; break; }
          }
          setCopyIndex(idx);
          setDayCount(Math.floor(p * 42));
          setBrainIntensity(p);
          setIsSpiking(idx >= 1 && idx <= 3);
        },
      });

      // ACT 1 → ACT 2 crossfade
      ScrollTrigger.create({
        trigger: act2Ref.current,
        start: 'top 80%', end: 'top 20%', scrub: 0.8,
        onUpdate: (self) => {
          setAct1Opacity(1 - self.progress);
          setAct2Opacity(self.progress);
        },
      });

      // ACT 2: Test progression
      ScrollTrigger.create({
        trigger: act2Ref.current,
        start: 'top top', end: 'bottom top', scrub: 0.6,
        onUpdate: (self) => {
          const p = self.progress;
          const N = TESTS.length;
          const slot = 1 / N;
          const newProgresses = TESTS.map((_, i) => {
            return Math.max(0, Math.min(1, (p - i * slot) / slot));
          });
          setTestProgresses(newProgresses);
          const activeIdx = Math.min(N - 1, Math.floor(p * N));
          setActiveTestIdx(p > 0 ? activeIdx : -1);
        },
      });

      // ACT 2 → ACT 3 crossfade
      ScrollTrigger.create({
        trigger: act3Ref.current,
        start: 'top 80%', end: 'top 20%', scrub: 0.8,
        onUpdate: (self) => {
          setAct2Opacity(1 - self.progress);
          setAct3Opacity(self.progress);
        },
      });

      // ACT 3: Insurance reveals
      ScrollTrigger.create({
        trigger: act3Ref.current,
        start: '5% top', end: 'bottom bottom', scrub: 0.5,
        onUpdate: (self) => {
          setInsuranceReveal(Math.floor(self.progress * INSURANCE_EVENTS.length));
        },
      });

      // ACT 3 → ACT 4 crossfade
      ScrollTrigger.create({
        trigger: act4Ref.current,
        start: 'top 75%', end: 'top 25%', scrub: 0.8,
        onUpdate: (self) => {
          setAct3Opacity(1 - self.progress);
          setAct4Opacity(self.progress);
        },
      });

      // ACT 4: Journey timeline reveal
      ScrollTrigger.create({
        trigger: act4Ref.current,
        start: '5% top', end: 'bottom bottom', scrub: 0.5,
        onUpdate: (self) => {
          setJourneyReveal(Math.floor(self.progress * MILESTONES.length));
        },
      });

    }, wrapRef);

    return () => ctx.revert();
  }, []);

  const currentLine = ACT1_LINES[copyIndex];
  const isBigMoment = copyIndex >= 4;
  const activeTest = activeTestIdx >= 0 ? TESTS[activeTestIdx] : null;
  const totalCost = CUMULATIVE_COSTS.reduce((s, c) => s + c.amount, 0);

  // Denial count for act 3 sidebar
  const denialCount = INSURANCE_EVENTS.slice(0, insuranceReveal).filter(e => e.type === 'denial').length;
  const approvalCount = INSURANCE_EVENTS.slice(0, insuranceReveal).filter(e => e.type === 'approval').length;

  return (
    <div ref={wrapRef} style={{ background: '#07080a', color: '#dde3f0', fontFamily: "'Syne', sans-serif", overflowX: 'clip' }}>

      {/* ══ ACT 1: FIRST SEIZURE ═════════════════════════════════════════════ */}
      <section ref={act1Ref} style={{ height: '500vh', position: 'relative' }}>
        <div style={{
          position: 'sticky', top: 0, height: '100vh',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden', opacity: act1Opacity, transition: 'opacity 0.1s',
        }}>
          <div style={{ position: 'absolute', inset: 0 }}>
            <HeartbeatCanvas intensity={brainIntensity} spiking={isSpiking} />
          </div>
          <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 60% 70% at 50% 50%, rgba(7,8,10,0.4) 0%, rgba(7,8,10,0.85) 100%)', pointerEvents: 'none' }} />

          <div style={{ position: 'relative', zIndex: 2, textAlign: 'center', marginBottom: '1.5rem' }}>
            <DayCounter days={dayCount} />
          </div>

          <div style={{ position: 'relative', zIndex: 2, maxWidth: '560px', textAlign: 'center', padding: '0 2rem' }}>
            <div ref={copyRef}>
              <p style={{
                ...(isBigMoment ? S.serif : S.mono),
                fontSize: isBigMoment ? 'clamp(1.4rem, 3vw, 2.2rem)' : 'clamp(0.8rem, 1.5vw, 1rem)',
                lineHeight: isBigMoment ? 1.25 : 1.85,
                color: isBigMoment ? '#dde3f0' : '#5a637a',
                whiteSpace: 'pre-line',
              }}>
                {currentLine.text}
              </p>
            </div>
          </div>

          {copyIndex === 0 && (
            <div style={{ position: 'absolute', bottom: '2.5rem', ...S.mono, fontSize: '0.6rem', letterSpacing: '0.2em', color: '#3a4255', textTransform: 'uppercase', animation: 'ejBounce 2s ease infinite' }}>
              ↓ scroll
            </div>
          )}
        </div>
      </section>

      {/* Bridge 1→2 */}
      <Bridge
        eyebrow="The waiting begins"
        heading="Every test takes weeks.<br/><em style='color:#ff6b8a;font-style:italic'>Every week, she seizes.</em>"
        sub="The diagnostic pathway for pediatric epilepsy is a gauntlet of scheduling delays, insurance hurdles, and sequential testing that cannot run in parallel."
      />

      {/* ══ ACT 2: DIAGNOSTIC GAUNTLET ═══════════════════════════════════════ */}
      <section ref={act2Ref} style={{ height: '500vh', position: 'relative' }}>
        <div style={{
          position: 'sticky', top: 0, height: '100vh',
          display: 'grid', gridTemplateColumns: '1fr 1fr', alignItems: 'stretch',
          overflow: 'hidden', opacity: act2Opacity, transition: 'opacity 0.15s',
        }}>
          {/* Left: test cards */}
          <div style={{ padding: '4rem 2rem 4rem 6vw', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={S.label}>The Tests</div>
            <h2 style={{ ...S.serif, fontSize: 'clamp(1.6rem, 2.5vw, 2.2rem)', lineHeight: 1.1, marginBottom: '1.5rem' }}>
              One at a time.<br />
              <span style={{ color: '#ff6b8a' }}>Each one waits for the last.</span>
            </h2>
            {TESTS.map((test, i) => (
              <TestCard
                key={test.id}
                test={test}
                progress={testProgresses[i]}
                isActive={i === activeTestIdx}
              />
            ))}
          </div>

          {/* Right: active test detail + running clock */}
          <div style={{ padding: '4rem 6vw 4rem 2rem', borderLeft: '1px solid rgba(255,255,255,0.05)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            {!activeTest ? (
              <div>
                <div style={S.label}>The Clock</div>
                <h2 style={{ ...S.serif, fontSize: 'clamp(1.8rem, 3vw, 2.8rem)', lineHeight: 1.1, marginBottom: '1.2rem' }}>
                  While they test,<br />she seizes.
                </h2>
                <p style={{ ...S.mono, fontSize: '0.8rem', lineHeight: 1.85, ...S.muted }}>
                  Each test is ordered sequentially. An EEG must come before an MRI is justified. An MRI must be normal before genetic testing is considered. The system was not designed for urgency.
                </p>
              </div>
            ) : (
              <div style={{ animation: 'ejFadeUp 0.4s ease' }}>
                <div style={{ display: 'inline-block', ...S.mono, fontSize: '0.6rem', letterSpacing: '0.1em', textTransform: 'uppercase', padding: '0.3rem 0.8rem', border: `1px solid ${activeTest.color}40`, background: `${activeTest.color}12`, color: activeTest.color, marginBottom: '1rem', borderRadius: '2px' }}>
                  Current Test
                </div>
                <h2 style={{ ...S.serif, fontSize: 'clamp(1.6rem, 2.5vw, 2.4rem)', lineHeight: 1.1, marginBottom: '0.6rem', color: activeTest.color }}>
                  {activeTest.icon} {activeTest.name}
                </h2>
                <div style={{ ...S.mono, fontSize: '1.4rem', color: activeTest.color, marginBottom: '1rem' }}>
                  {activeTest.duration}
                  <span style={{ fontSize: '0.5em', color: '#3a4255', marginLeft: '0.5rem' }}>wait time</span>
                </div>
                <p style={{ ...S.mono, fontSize: '0.78rem', lineHeight: 1.85, ...S.muted, marginBottom: '1.5rem' }}>
                  {activeTest.desc}
                </p>
                <div style={{ padding: '1rem', background: 'rgba(255,107,138,0.06)', border: '1px solid rgba(255,107,138,0.2)', borderRadius: '2px' }}>
                  <div style={{ ...S.mono, fontSize: '0.55rem', letterSpacing: '0.15em', color: '#ff6b8a', textTransform: 'uppercase', marginBottom: '0.3rem' }}>
                    Insurance Note
                  </div>
                  <div style={{ ...S.mono, fontSize: '0.72rem', color: '#ff6b8a', lineHeight: 1.6, opacity: 0.85 }}>
                    {activeTest.insurance}
                  </div>
                </div>

                {/* Cumulative time bar */}
                <div style={{ marginTop: '2rem' }}>
                  <div style={{ ...S.mono, fontSize: '0.55rem', letterSpacing: '0.15em', color: '#3a4255', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
                    Cumulative Wait
                  </div>
                  <div style={{ display: 'flex', gap: '2px', height: '20px' }}>
                    {TESTS.slice(0, activeTestIdx + 1).map((t, j) => (
                      <div key={t.id} style={{
                        flex: t.daysMax,
                        background: j <= activeTestIdx ? t.color : 'rgba(255,255,255,0.04)',
                        opacity: j < activeTestIdx ? 0.5 : 1,
                        borderRadius: '1px',
                        transition: 'all 0.4s',
                      }} />
                    ))}
                  </div>
                  <div style={{ ...S.mono, fontSize: '0.7rem', color: '#dde3f0', marginTop: '0.4rem' }}>
                    {TESTS.slice(0, activeTestIdx + 1).reduce((s, t) => s + t.daysMax, 0)} days
                    <span style={{ color: '#3a4255' }}> maximum elapsed</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Bridge 2→3 */}
      <Bridge
        eyebrow="But the tests are only half the battle"
        heading="Every test needs<br/><em style='color:#ff6b8a;font-style:italic'>permission to exist.</em>"
        sub="Insurance companies require prior authorization for many diagnostic tests. When they deny coverage, families face impossible choices between waiting months for appeals — or paying thousands out of pocket."
      />

      {/* ══ ACT 3: INSURANCE BATTLES ═════════════════════════════════════════ */}
      <section ref={act3Ref} style={{ minHeight: '450vh', position: 'relative' }}>
        <div style={{
          position: 'sticky', top: 0, height: '100vh',
          overflow: 'hidden', opacity: act3Opacity, transition: 'opacity 0.15s',
          display: 'grid', gridTemplateColumns: '1fr 2fr', alignItems: 'start',
        }}>
          {/* Left: fixed summary */}
          <div style={{ padding: '6rem 3rem 4rem 6vw', position: 'sticky', top: 0 }}>
            <div style={S.label}>Insurance</div>
            <h2 style={{ ...S.serif, fontSize: 'clamp(1.8rem, 3vw, 2.4rem)', lineHeight: 1.1, marginBottom: '1.5rem' }}>
              The system<br />says <em style={{ color: '#ff6b8a', fontStyle: 'italic' }}>no.</em>
            </h2>
            <p style={{ ...S.mono, fontSize: '0.78rem', lineHeight: 1.85, ...S.muted, marginBottom: '2rem' }}>
              Prior authorizations. Peer-to-peer reviews. Appeals. External reviews. Each "no" costs weeks. Each week costs seizures.
            </p>

            {/* Live tally */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
              <div>
                <div style={{ ...S.mono, fontSize: '0.55rem', letterSpacing: '0.2em', color: '#3a4255', textTransform: 'uppercase', marginBottom: '0.2rem' }}>
                  Denials
                </div>
                <div style={{ ...S.mono, fontSize: '2rem', color: '#ff6b8a' }}>
                  {denialCount}
                </div>
              </div>
              <div>
                <div style={{ ...S.mono, fontSize: '0.55rem', letterSpacing: '0.2em', color: '#3a4255', textTransform: 'uppercase', marginBottom: '0.2rem' }}>
                  Approvals
                </div>
                <div style={{ ...S.mono, fontSize: '2rem', color: '#00e5b0' }}>
                  {approvalCount}
                </div>
              </div>
              <div style={{ width: '100%', height: '1px', background: 'rgba(255,255,255,0.06)' }} />
              <div>
                <div style={{ ...S.mono, fontSize: '0.55rem', letterSpacing: '0.2em', color: '#3a4255', textTransform: 'uppercase', marginBottom: '0.2rem' }}>
                  Average delay per denial
                </div>
                <div style={{ ...S.mono, fontSize: '1.2rem', color: '#f5a623' }}>
                  6–8 weeks
                </div>
              </div>
            </div>

            <div style={{ marginTop: '2.5rem', padding: '1rem', background: 'rgba(255,107,138,0.04)', border: '1px solid rgba(255,107,138,0.15)' }}>
              <div style={{ ...S.mono, fontSize: '0.65rem', color: '#ff6b8a', marginBottom: '0.3rem', letterSpacing: '0.1em' }}>
                While they appeal
              </div>
              <div style={{ ...S.mono, fontSize: '0.78rem', ...S.muted, lineHeight: 1.6 }}>
                Mia continues to seize. Her development falls behind. Her parents haven't slept a full night in months.
              </div>
            </div>
          </div>

          {/* Right: scrolling insurance timeline */}
          <div ref={act3PanelRef} style={{ padding: '4rem 6vw 4rem 2rem', overflowY: 'auto', height: '100vh' }}>
            <InsuranceTimeline revealCount={insuranceReveal} />
          </div>
        </div>
      </section>

      {/* Bridge 3→4 */}
      <Bridge
        eyebrow="The full picture"
        heading="Eighteen months.<br/><em style='color:#ff6b8a;font-style:italic'>The answer was a vitamin.</em>"
        sub="Pyridoxine-dependent epilepsy is treatable with vitamin B6. But it takes an average of 18 months — and thousands of dollars — to reach that diagnosis through the current system."
      />

      {/* ══ ACT 4: THE FULL JOURNEY ═══════════════════════════════════════════ */}
      <section ref={act4Ref} style={{ minHeight: '500vh', position: 'relative' }}>
        <div style={{
          position: 'sticky', top: 0, height: '100vh',
          overflow: 'hidden', opacity: act4Opacity, transition: 'opacity 0.15s',
          display: 'grid', gridTemplateColumns: '1fr 2fr', alignItems: 'start',
        }}>
          {/* Left: summary + costs */}
          <div style={{ padding: '5rem 3rem 4rem 6vw', position: 'sticky', top: 0 }}>
            <div style={S.label}>The Journey</div>
            <h2 style={{ ...S.serif, fontSize: 'clamp(1.8rem, 3vw, 2.4rem)', lineHeight: 1.1, marginBottom: '1rem' }}>
              18 months of<br />
              <em style={{ color: '#ff6b8a', fontStyle: 'italic' }}>preventable suffering.</em>
            </h2>
            <p style={{ ...S.mono, fontSize: '0.78rem', lineHeight: 1.85, ...S.muted, marginBottom: '2rem' }}>
              From first seizure to diagnosis. Every step could have been faster. Every delay was systemic.
            </p>

            {/* Cost breakdown */}
            <div style={{ ...S.mono, fontSize: '0.55rem', letterSpacing: '0.2em', color: '#3a4255', textTransform: 'uppercase', marginBottom: '0.8rem' }}>
              Family Cost Burden
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', marginBottom: '1.2rem' }}>
              {CUMULATIVE_COSTS.map((c) => (
                <div key={c.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ ...S.mono, fontSize: '0.65rem', color: '#5a637a' }}>{c.label}</span>
                  <span style={{ ...S.mono, fontSize: '0.65rem', color: '#ff6b8a' }}>{formatMoney(c.amount)}</span>
                </div>
              ))}
            </div>
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '0.6rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ ...S.syne, fontSize: '0.85rem', color: '#dde3f0', fontWeight: 600 }}>Total</span>
              <span style={{ ...S.mono, fontSize: '1.3rem', color: '#ff6b8a', fontWeight: 500 }}>{formatMoney(totalCost)}</span>
            </div>

            <div style={{ marginTop: '2rem', padding: '1rem', background: 'rgba(0,229,176,0.04)', border: '1px solid rgba(0,229,176,0.15)' }}>
              <div style={{ ...S.mono, fontSize: '0.65rem', color: '#00e5b0', marginBottom: '0.3rem', letterSpacing: '0.1em' }}>
                With early genetic testing
              </div>
              <div style={{ ...S.mono, fontSize: '0.78rem', ...S.muted, lineHeight: 1.6 }}>
                Diagnosis in weeks, not months. No unnecessary medication trials. No insurance battles. Cost: a fraction of the total.
              </div>
            </div>

            {/* Legend */}
            <div style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {[['#ff6b8a', 'Denial / Setback'], ['#f5a623', 'Medication trial'], ['#00e5b0', 'Progress / Resolution'], ['#b48cfa', 'Testing'], ['#5bc8f5', 'Imaging'], ['#5a637a', 'Waiting']].map(([col, lbl]) => (
                <div key={lbl} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: col, flexShrink: 0 }} />
                  <span style={{ ...S.mono, fontSize: '0.62rem', color: '#5a637a', letterSpacing: '0.06em' }}>{lbl}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: journey timeline */}
          <div ref={act4PanelRef} style={{ padding: '4rem 6vw 4rem 2rem', overflowY: 'auto', height: '100vh' }}>
            <JourneyTimeline revealCount={journeyReveal} />
          </div>
        </div>
      </section>

      {/* ══ CLOSING ═══════════════════════════════════════════════════════════ */}
      <div style={{ textAlign: 'center', padding: '10rem 2rem 8rem', background: 'linear-gradient(to bottom, #07080a 0%, #0b0f1a 100%)', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <p style={{ ...S.mono, fontSize: '0.6rem', letterSpacing: '0.2em', color: '#ff6b8a', textTransform: 'uppercase', marginBottom: '2rem' }}>
          This is not rare
        </p>
        <p style={{ ...S.serif, fontSize: 'clamp(2.2rem, 5vw, 4.5rem)', lineHeight: 1.05, maxWidth: '800px', margin: '0 auto 2rem', letterSpacing: '-0.02em' }}>
          1 in 26 people<br />
          will develop epilepsy.
        </p>
        <p style={{ ...S.mono, fontSize: 'clamp(0.8rem, 1.4vw, 1rem)', ...S.muted, maxWidth: '520px', margin: '0 auto 2rem', lineHeight: 1.85 }}>
          470,000 children in the US live with epilepsy. Many wait months or years for a diagnosis that could take weeks with the right testing pathway.
        </p>

        <div style={{ maxWidth: '600px', margin: '3rem auto 3rem', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '2rem' }}>
          {[
            { stat: '18 mo', label: 'Average time to diagnosis for genetic epilepsies', color: '#ff6b8a' },
            { stat: '$32K+', label: 'Average family cost burden during diagnostic odyssey', color: '#f5a623' },
            { stat: '47%', label: 'Of genetic test prior authorizations initially denied', color: '#b48cfa' },
          ].map((s) => (
            <div key={s.label}>
              <div style={{ ...S.mono, fontSize: 'clamp(1.5rem, 3vw, 2.2rem)', color: s.color, marginBottom: '0.5rem' }}>
                {s.stat}
              </div>
              <div style={{ ...S.mono, fontSize: '0.6rem', color: '#5a637a', lineHeight: 1.6 }}>
                {s.label}
              </div>
            </div>
          ))}
        </div>

        <p style={{ ...S.serif, fontSize: 'clamp(1.4rem, 3vw, 2.2rem)', color: '#dde3f0', maxWidth: '680px', margin: '3rem auto 1.5rem', lineHeight: 1.2 }}>
          The science exists to diagnose faster.<br />
          <em style={{ color: '#00e5b0', fontStyle: 'italic' }}>The system just hasn't caught up.</em>
        </p>
        <p style={{ ...S.serif, fontSize: 'clamp(1rem, 2vw, 1.5rem)', color: '#3a4255', fontStyle: 'italic', marginTop: '3rem' }}>
          What if Mia didn't have to wait?
        </p>
      </div>

      {/* ══ HOW YOU CAN HELP ══════════════════════════════════════════════════ */}
      <HelpSection />

      {/* ══ SOURCES ═══════════════════════════════════════════════════════════ */}
      <SourcesSection />

      <ContactFooter />

      <style>{`
        @keyframes ejBounce { 0%,100%{transform:translateY(0)} 50%{transform:translateY(6px)} }
        @keyframes ejFadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
        @keyframes fadeInUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
      `}</style>
    </div>
  );
}
